import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Influencer } from "@/types/influencer";
import { SearchBar } from "@/components/influencer/SearchBar";
import { CategoryFilter } from "@/components/influencer/CategoryFilter";
import { BookingPageHeader } from "@/components/influencer/BookingPageHeader";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const InfluencerBooking = () => {
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const { data, error } = await supabase
          .from('influencers')
          .select('*')
          .eq('approval_status', 'approved')
          .order('featured', { ascending: false })
          .order('rating', { ascending: false });

        if (error) throw error;

        const mappedInfluencers: Influencer[] = (data || []).map((inf) => ({
          id: inf.id,
          name: inf.name,
          category: inf.category,
          followers: inf.followers,
          price: inf.price,
          availability: inf.availability,
          image: inf.image || '/placeholder.svg',
          rating: inf.rating,
          featured: inf.featured,
          description: inf.description || undefined,
          expertise: inf.expertise || undefined,
          socialLinks: inf.social_links as Influencer['socialLinks'],
          contactInfo: inf.contact_info as Influencer['contactInfo'],
        }));

        setInfluencers(mappedInfluencers);
      } catch (error) {
        console.error('Error fetching influencers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInfluencers();
  }, []);
  
  const filteredInfluencers = influencers.filter((influencer) => {
    const matchesSearch = 
      influencer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      influencer.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = 
      activeCategory === "all" || 
      (activeCategory === "featured" && influencer.featured) ||
      influencer.category.toLowerCase() === activeCategory.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: "all", name: language === 'ar' ? 'جميع المؤثرين' : "All Influencers" },
    { id: "featured", name: language === 'ar' ? 'المميزون' : "Featured" },
  ];

  return (
    <motion.div 
      className={`min-h-[calc(100vh-8rem)] px-4 py-12 ${language === 'ar' ? 'text-right' : 'text-left'}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto">
        <BookingPageHeader />
        
        <motion.div 
          className="mb-12 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-background/50 backdrop-blur-md border border-primary/5 rounded-2xl p-6 shadow-lg">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              <SearchBar 
                searchQuery={searchQuery} 
                onSearchChange={setSearchQuery} 
              />
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <CategoryFilter 
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            filteredInfluencers={filteredInfluencers}
          />
        )}
      </div>
    </motion.div>
  );
};

export default InfluencerBooking;
